import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Chip,
    CircularProgress,
    InputBase,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { AddRounded, CloseRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import ObjectIcon from "../components/ObjectIcon";
import CreateRecordAction from "../components/CreateRecordAction";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import { Button } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import {
    createWorkflow,
    listPipelines,
    listWorkflows,
    updateWorkflow,
} from "../api";
import {
    ACTION_TYPES,
    OPS,
    TRIGGER_TYPES,
    actionSummary,
    apiError,
    opNeedsValue,
    triggerLabel,
} from "../utils/meta";

const NODE_W = 216;
const NODE_H = 76;
const NODE_Y = 120;
const NODE_X0 = 48;
const NODE_GAP = 72;

const PANEL_LABEL_SX = {
    fontSize: 11,
    letterSpacing: "2px",
    fontWeight: 700,
    color: "var(--text-tertiary)",
};

const SECTION_SX = {
    fontSize: 12,
    letterSpacing: "1.5px",
    fontWeight: 700,
    color: "var(--text-strong-muted)",
};

const nodeX = (i) => NODE_X0 + i * (NODE_W + NODE_GAP);

const inputSx = { fontSize: 13, background: "var(--bg-secondary)" };

function ChainNode({ x, badge, badgeColor, title, subtitle, selected, dashed, onClick }) {
    return (
        <Stack
            onClick={onClick}
            sx={{
                position: "absolute",
                left: x,
                top: NODE_Y,
                width: NODE_W,
                height: NODE_H,
                background: dashed ? "transparent" : "var(--bg-secondary)",
                border: selected
                    ? "1.5px solid var(--accent)"
                    : dashed
                      ? "1px dashed var(--border-strong)"
                      : "1px solid var(--border-mid)",
                borderRadius: "6px",
                boxShadow: selected
                    ? "0 2px 10px rgba(30,107,79,0.12)"
                    : dashed
                      ? "none"
                      : "0 1px 4px rgba(23,23,21,0.05)",
                justifyContent: "center",
                gap: 0.4,
                px: 2,
                cursor: "pointer",
                userSelect: "none",
            }}
        >
            <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
                {badge && (
                    <Typography
                        sx={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            letterSpacing: "1.5px",
                            color: badgeColor,
                        }}
                    >
                        {badge}
                    </Typography>
                )}
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: dashed ? "var(--text-secondary)" : "var(--text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {title}
                </Typography>
            </Stack>
            {subtitle && (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: "var(--text-tertiary)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Stack>
    );
}

/* ------------------------------------------------------------------ */

export default function WorkflowBuilder() {
    const { id } = useParams();
    const isNew = !id;
    const navigate = useNavigate();
    const { objects, loading: objectsLoading } = useObjects();

    const [name, setName] = useState("Untitled workflow");
    const [description, setDescription] = useState("");
    const [objectId, setObjectId] = useState("");
    const [trigger, setTrigger] = useState({ type: "record.created" });
    const [conditions, setConditions] = useState([]);
    const [actions, setActions] = useState([
        { type: "update_field", field: "", value: "" },
    ]);
    const [isActive, setIsActive] = useState(true);
    const [canvas, setCanvas] = useState(null);
    const [selected, setSelected] = useState("when");
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [pipelineStages, setPipelineStages] = useState([]);
    const [stagesLoading, setStagesLoading] = useState(false);

    const object = objects.find((o) => o.id === objectId) ?? null;
    const fields = useMemo(
        () =>
            [...(object?.fields ?? [])].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
            ),
        [object],
    );

    /* A stage-change trigger can name the exact move, so the panel needs the
       object's own stages. Pipelines are per-object, so refetch when it changes. */
    useEffect(() => {
        if (!objectId) {
            setPipelineStages([]);
            return undefined;
        }
        let alive = true;
        setStagesLoading(true);
        (async () => {
            try {
                const { data } = await listPipelines(objectId);
                const pipelines = Array.isArray(data) ? data : [];
                const preferred =
                    pipelines.find((p) => p.isDefault) ?? pipelines[0] ?? null;
                if (alive) {
                    setPipelineStages(
                        [...(preferred?.stages ?? [])].sort(
                            (a, b) => (a.position ?? 0) - (b.position ?? 0),
                        ),
                    );
                }
            } catch (error) {
                console.error("Failed to load pipeline stages", { error });
                if (alive) setPipelineStages([]);
            } finally {
                if (alive) setStagesLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [objectId]);

    /* Load an existing workflow (the API exposes only the list). */
    useEffect(() => {
        if (isNew) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await listWorkflows();
                const list = Array.isArray(data) ? data : (data?.data ?? []);
                const wf = list.find((w) => String(w.id) === String(id));
                if (!wf) {
                    toast.error("Workflow not found");
                    navigate("/admin/workflows");
                    return;
                }
                setName(wf.name ?? "");
                setDescription(wf.description ?? "");
                setObjectId(wf.objectId ?? "");
                setTrigger(wf.trigger ?? { type: "record.created" });
                setConditions(wf.conditions?.all ?? []);
                setActions(
                    wf.actions?.length
                        ? wf.actions
                        : [{ type: "update_field", field: "", value: "" }],
                );
                setIsActive(wf.isActive !== false);
                setCanvas(wf.canvas ?? null);
                setDirty(false);
            } catch (err) {
                toast.error(apiError(err, "Failed to load the workflow"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isNew, navigate]);

    /* Default the object for new workflows once meta loads. */
    useEffect(() => {
        if (isNew && !objectId && objects.length > 0) {
            setObjectId(objects[0].id);
        }
    }, [isNew, objectId, objects]);

    /* Unsaved-changes guard. */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const touch = () => setDirty(true);

    const updateAction = (i, patch) => {
        setActions((prev) =>
            prev.map((a, ai) => (ai === i ? { ...a, ...patch } : a)),
        );
        touch();
    };

    const updateCondition = (i, patch) => {
        setConditions((prev) =>
            prev.map((c, ci) => (ci === i ? { ...c, ...patch } : c)),
        );
        touch();
    };

    async function handleSave() {
        if (!name.trim()) {
            toast.error("Give the workflow a name");
            return;
        }
        if (!objectId) {
            toast.error("Pick the object this workflow watches");
            return;
        }
        if (actions.length === 0) {
            toast.error("Add at least one action");
            return;
        }
        setSaving(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || undefined,
                objectId,
                trigger,
                conditions: conditions.length ? { all: conditions } : null,
                actions,
                isActive,
                canvas: canvas ?? undefined,
            };
            if (isNew) {
                await createWorkflow(body);
                toast.success(`Workflow "${body.name}" created`);
            } else {
                await updateWorkflow(id, body);
                toast.success(`Workflow "${body.name}" saved`);
            }
            navigate("/admin/workflows");
        } catch (err) {
            toast.error(apiError(err, "Could not save the workflow"));
        } finally {
            setSaving(false);
        }
    }

    if (objectsLoading || loading) {
        return (
            <Stack sx={{ alignItems: "center", py: 12 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    /* Chain layout: WHEN → IF → THEN… (+ add) */
    const thenStart = 2;
    const addNodeX = nodeX(thenStart + actions.length);
    const arrowCount = thenStart + actions.length - 1;

    const selectedActionIndex =
        typeof selected === "number" ? selected : null;

    return (
        <Stack
            sx={{
                height: "calc(100vh - 60px - 48px)",
                minHeight: 540,
                border: "1px solid var(--border)",
                borderRadius: "6px",
                overflow: "hidden",
                background: "var(--bg-secondary)",
            }}
        >
            {/* ---------- top bar ---------- */}
            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    height: 58,
                    px: 2.5,
                    borderBottom: "1px solid var(--border)",
                    flexShrink: 0,
                }}
            >
                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1.5,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <InputBase
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            touch();
                        }}
                        sx={{
                            fontSize: 14.5,
                            fontWeight: 600,
                            width: 240,
                            flexShrink: 0,
                            "& input": { p: 0 },
                        }}
                    />
                    <InputBase
                        value={description}
                        placeholder="Add a description…"
                        onChange={(e) => {
                            setDescription(e.target.value);
                            touch();
                        }}
                        sx={{
                            fontSize: 13,
                            color: "var(--text-secondary)",
                            flex: 1,
                            minWidth: 0,
                            "& input": { p: 0 },
                        }}
                    />
                    {dirty && (
                        <Chip
                            size="small"
                            label="DRAFT"
                            sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: "0.8px",
                                background: "var(--bg-tint-soft)",
                                border: "1px solid var(--border-mid)",
                                color: "var(--text-secondary)",
                            }}
                        />
                    )}
                </Stack>
                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "var(--text-secondary)",
                            }}
                        >
                            Active
                        </Typography>
                        <Switch
                            size="small"
                            checked={isActive}
                            onChange={(e) => {
                                setIsActive(e.target.checked);
                                touch();
                            }}
                        />
                    </Stack>
                    {!isNew && (
                        <Button
                            variant="text"
                            sx={{ height: 38 }}
                            onClick={() => setHistoryOpen(true)}
                        >
                            History
                        </Button>
                    )}
                    <Button
                        variant="soft-outlined"
                        sx={{ height: 38 }}
                        onClick={() => navigate("/admin/workflows")}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        loading={saving}
                        onClick={handleSave}
                        sx={{ height: 38 }}
                    >
                        Save workflow
                    </Button>
                </Stack>
            </Stack>

            <Stack sx={{ flexDirection: "row", flex: 1, minHeight: 0 }}>
                {/* ---------- canvas ---------- */}
                <Stack
                    onClick={() => setSelected(null)}
                    sx={{
                        flex: 1,
                        position: "relative",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden",
                        backgroundColor: "var(--bg)",
                        backgroundImage:
                            "radial-gradient(var(--border-mid) 1px, transparent 1px)",
                        backgroundSize: "22px 22px",
                    }}
                >
                    <Stack
                        sx={{
                            position: "relative",
                            minWidth: addNodeX + NODE_W + 48,
                            height: "100%",
                        }}
                    >
                        <svg
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                pointerEvents: "none",
                            }}
                        >
                            <defs>
                                <marker
                                    id="wf-arrow"
                                    markerWidth="9"
                                    markerHeight="9"
                                    refX="7.5"
                                    refY="4.5"
                                    orient="auto"
                                >
                                    <path
                                        d="M0.5 1 7.5 4.5 0.5 8"
                                        fill="none"
                                        stroke="#8B8781"
                                        strokeWidth="1.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </marker>
                            </defs>
                            {Array.from({ length: arrowCount }).map((_, i) => {
                                const sx1 = nodeX(i) + NODE_W;
                                const ex1 = nodeX(i + 1);
                                const y = NODE_Y + NODE_H / 2;
                                const off = Math.max(
                                    24,
                                    (ex1 - sx1) * 0.45,
                                );
                                return (
                                    <path
                                        key={i}
                                        d={`M ${sx1} ${y} C ${sx1 + off} ${y}, ${ex1 - off} ${y}, ${ex1} ${y}`}
                                        stroke="#8B8781"
                                        strokeWidth="1.4"
                                        fill="none"
                                        markerEnd="url(#wf-arrow)"
                                    />
                                );
                            })}
                        </svg>

                        <ChainNode
                            x={nodeX(0)}
                            badge="WHEN"
                            badgeColor="var(--accent)"
                            title={object?.name ?? "Pick an object"}
                            subtitle={triggerLabel(trigger)}
                            selected={selected === "when"}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected("when");
                            }}
                        />
                        <ChainNode
                            x={nodeX(1)}
                            badge="IF"
                            badgeColor="var(--warning)"
                            title={
                                conditions.length
                                    ? `${conditions.length} condition${conditions.length === 1 ? "" : "s"}`
                                    : "No conditions"
                            }
                            subtitle={
                                conditions.length
                                    ? "All must match"
                                    : "Runs on every trigger"
                            }
                            selected={selected === "if"}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected("if");
                            }}
                        />
                        {actions.map((a, i) => (
                            <ChainNode
                                key={i}
                                x={nodeX(thenStart + i)}
                                badge="THEN"
                                badgeColor="var(--info)"
                                title={
                                    ACTION_TYPES.find(
                                        (t) => t.key === a.type,
                                    )?.label ?? a.type
                                }
                                subtitle={actionSummary(a, fields, objects)}
                                selected={selectedActionIndex === i}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelected(i);
                                }}
                            />
                        ))}
                        <ChainNode
                            x={addNodeX}
                            title="+ Action"
                            subtitle="Add another step"
                            dashed
                            onClick={(e) => {
                                e.stopPropagation();
                                setActions((prev) => [
                                    ...prev,
                                    {
                                        type: "update_field",
                                        field: fields[0]?.key ?? "",
                                        value: "",
                                    },
                                ]);
                                setSelected(actions.length);
                                touch();
                            }}
                        />
                    </Stack>
                </Stack>

                {/* ---------- right panel ---------- */}
                <Stack
                    sx={{
                        width: 336,
                        flexShrink: 0,
                        borderLeft: "1px solid var(--border)",
                        overflowY: "auto",
                    }}
                >
                    {selected === "when" && (
                        <Stack sx={{ p: "22px 24px", gap: 2.5 }}>
                            <Typography sx={PANEL_LABEL_SX}>
                                WHEN — TRIGGER
                            </Typography>
                            <Stack sx={{ gap: 1 }}>
                                <Typography sx={SECTION_SX}>OBJECT</Typography>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={objectId}
                                    onChange={(e) => {
                                        setObjectId(e.target.value);
                                        setConditions([]);
                                        touch();
                                    }}
                                    sx={inputSx}
                                >
                                    {objects.map((o) => (
                                        <MenuItem
                                            key={o.id}
                                            value={o.id}
                                            sx={{ fontSize: 13.5 }}
                                        >
                                            <Stack
                                                sx={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <ObjectIcon
                                                    name={o.icon}
                                                    size={15}
                                                />
                                                {o.name}
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                            <Stack sx={{ gap: 1 }}>
                                <Typography sx={SECTION_SX}>
                                    TRIGGER
                                </Typography>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={trigger.type}
                                    onChange={(e) => {
                                        const type = e.target.value;
                                        setTrigger(
                                            type === "schedule"
                                                ? {
                                                      type,
                                                      config: {
                                                          everyMinutes: 60,
                                                      },
                                                  }
                                                : { type },
                                        );
                                        touch();
                                    }}
                                    sx={inputSx}
                                >
                                    {TRIGGER_TYPES.map((t) => (
                                        <MenuItem
                                            key={t.key}
                                            value={t.key}
                                            sx={{ fontSize: 13.5 }}
                                        >
                                            {t.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                            {trigger.type === "schedule" && (
                                <Stack sx={{ gap: 1 }}>
                                    <Typography sx={SECTION_SX}>
                                        EVERY N MINUTES
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        value={
                                            trigger.config?.everyMinutes ?? 60
                                        }
                                        onChange={(e) => {
                                            const raw = parseInt(
                                                e.target.value,
                                                10,
                                            );
                                            setTrigger({
                                                type: "schedule",
                                                config: {
                                                    everyMinutes:
                                                        Number.isNaN(raw)
                                                            ? 60
                                                            : Math.max(5, raw),
                                                },
                                            });
                                            touch();
                                        }}
                                        inputProps={{ min: 5 }}
                                        InputProps={{ sx: inputSx }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: "var(--text-tertiary)",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        Runs on a timer against every record of
                                        this object that matches the
                                        conditions. Minimum every 5 minutes.
                                    </Typography>
                                </Stack>
                            )}
                            {trigger.type === "record.updated" && (
                                <Stack sx={{ gap: 1 }}>
                                    <Typography sx={SECTION_SX}>
                                        SPECIFIC FIELD (OPTIONAL)
                                    </Typography>
                                    <Select
                                        size="small"
                                        fullWidth
                                        displayEmpty
                                        value={trigger.config?.field ?? ""}
                                        onChange={(e) => {
                                            const field = e.target.value;
                                            setTrigger({
                                                type: trigger.type,
                                                ...(field
                                                    ? { config: { field } }
                                                    : {}),
                                            });
                                            touch();
                                        }}
                                        sx={inputSx}
                                    >
                                        <MenuItem
                                            value=""
                                            sx={{ fontSize: 13.5 }}
                                        >
                                            Any field
                                        </MenuItem>
                                        {fields.map((f) => (
                                            <MenuItem
                                                key={f.key}
                                                value={f.key}
                                                sx={{ fontSize: 13.5 }}
                                            >
                                                {f.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Stack>
                            )}

                            {trigger.type === "record.stage_changed" && (
                                <Stack sx={{ gap: 1 }}>
                                    <Typography sx={SECTION_SX}>
                                        WHICH MOVE (OPTIONAL)
                                    </Typography>

                                    {stagesLoading ? (
                                        <CircularProgress size={16} />
                                    ) : pipelineStages.length === 0 ? (
                                        <Typography
                                            sx={{
                                                fontSize: 12.5,
                                                color: "var(--text-tertiary)",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {objectId
                                                ? "This object has no pipeline yet — the workflow will run on any stage change."
                                                : "Pick an object first."}
                                        </Typography>
                                    ) : (
                                        <>
                                            {[
                                                { key: "fromStageId", label: "From stage" },
                                                { key: "toStageId", label: "To stage" },
                                            ].map(({ key, label }) => (
                                                <Stack key={key} sx={{ gap: 0.5 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "var(--text-tertiary)",
                                                        }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        displayEmpty
                                                        value={
                                                            trigger.config?.[key] ?? ""
                                                        }
                                                        onChange={(e) => {
                                                            const next = {
                                                                ...(trigger.config ?? {}),
                                                                [key]: e.target.value,
                                                            };
                                                            /* Drop empty sides so the
                                                               stored trigger stays clean. */
                                                            Object.keys(next).forEach(
                                                                (k) =>
                                                                    !next[k] &&
                                                                    delete next[k],
                                                            );
                                                            setTrigger({
                                                                type: trigger.type,
                                                                ...(Object.keys(next).length
                                                                    ? { config: next }
                                                                    : {}),
                                                            });
                                                            touch();
                                                        }}
                                                        sx={inputSx}
                                                    >
                                                        <MenuItem
                                                            value=""
                                                            sx={{ fontSize: 13.5 }}
                                                        >
                                                            Any stage
                                                        </MenuItem>
                                                        {pipelineStages.map((s) => (
                                                            <MenuItem
                                                                key={s.id}
                                                                value={s.id}
                                                                sx={{ fontSize: 13.5 }}
                                                            >
                                                                {s.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Stack>
                                            ))}
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: "var(--text-tertiary)",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                Leave a side on “Any stage” to match every
                                                move in or out of the other one.
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}

                    {selected === "if" && (
                        <Stack sx={{ p: "22px 24px", gap: 2 }}>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography sx={PANEL_LABEL_SX}>
                                    IF — CONDITIONS
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "var(--text-tertiary)",
                                    }}
                                >
                                    All must match
                                </Typography>
                            </Stack>

                            {conditions.length === 0 && (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: "var(--text-secondary)",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    No conditions — the actions run every time
                                    the trigger fires.
                                </Typography>
                            )}

                            {conditions.map((c, i) => (
                                <Stack
                                    key={i}
                                    sx={{
                                        border: "1px solid var(--border-mid)",
                                        borderRadius: "4px",
                                        p: "10px 12px",
                                        gap: 1,
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            gap: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={c.field ?? ""}
                                            onChange={(e) =>
                                                updateCondition(i, {
                                                    field: e.target.value,
                                                })
                                            }
                                            sx={inputSx}
                                        >
                                            {fields.map((f) => (
                                                <MenuItem
                                                    key={f.key}
                                                    value={f.key}
                                                    sx={{ fontSize: 13 }}
                                                >
                                                    {f.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Stack
                                            onClick={() => {
                                                setConditions((prev) =>
                                                    prev.filter(
                                                        (_c, ci) => ci !== i,
                                                    ),
                                                );
                                                touch();
                                            }}
                                            sx={{
                                                cursor: "pointer",
                                                color: "var(--text-tertiary)",
                                                "&:hover": {
                                                    color: "var(--error)",
                                                },
                                            }}
                                        >
                                            <CloseRounded
                                                sx={{ fontSize: 16 }}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Stack
                                        sx={{ flexDirection: "row", gap: 1 }}
                                    >
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={c.op ?? "not_empty"}
                                            onChange={(e) =>
                                                updateCondition(i, {
                                                    op: e.target.value,
                                                })
                                            }
                                            sx={inputSx}
                                        >
                                            {OPS.map((o) => (
                                                <MenuItem
                                                    key={o.key}
                                                    value={o.key}
                                                    sx={{ fontSize: 13 }}
                                                >
                                                    {o.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {opNeedsValue(c.op) && (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Value"
                                                value={c.value ?? ""}
                                                onChange={(e) =>
                                                    updateCondition(i, {
                                                        value: e.target.value,
                                                    })
                                                }
                                                InputProps={{ sx: inputSx }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            ))}

                            <Stack
                                onClick={() => {
                                    setConditions((prev) => [
                                        ...prev,
                                        {
                                            field: fields[0]?.key ?? "",
                                            op: "not_empty",
                                            value: "",
                                        },
                                    ]);
                                    touch();
                                }}
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor: "pointer",
                                    color: "var(--accent)",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    width: "fit-content",
                                }}
                            >
                                <AddRounded sx={{ fontSize: 15 }} />
                                Add condition
                            </Stack>
                        </Stack>
                    )}

                    {selectedActionIndex != null &&
                        actions[selectedActionIndex] &&
                        (() => {
                            const a = actions[selectedActionIndex];
                            const i = selectedActionIndex;
                            return (
                                <Stack sx={{ p: "22px 24px", gap: 2.5 }}>
                                    <Typography sx={PANEL_LABEL_SX}>
                                        THEN — ACTION {i + 1}
                                    </Typography>

                                    <Stack sx={{ gap: 1 }}>
                                        <Typography sx={SECTION_SX}>
                                            DO THIS
                                        </Typography>
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={a.type}
                                            onChange={(e) => {
                                                const type = e.target.value;
                                                const base = { type };
                                                if (type === "update_field") {
                                                    base.field =
                                                        fields[0]?.key ?? "";
                                                    base.value = "";
                                                }
                                                if (type === "send_email") {
                                                    base.to = "owner";
                                                    base.subject = "";
                                                    base.body = "";
                                                }
                                                if (type === "webhook")
                                                    base.url = "";
                                                setActions((prev) =>
                                                    prev.map((act, ai) =>
                                                        ai === i ? base : act,
                                                    ),
                                                );
                                                touch();
                                            }}
                                            sx={inputSx}
                                        >
                                            {ACTION_TYPES.map((t) => (
                                                <MenuItem
                                                    key={t.key}
                                                    value={t.key}
                                                    sx={{ fontSize: 13.5 }}
                                                >
                                                    {t.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Stack>

                                    {a.type === "update_field" && (
                                        <Stack sx={{ gap: 1.5 }}>
                                            <Select
                                                size="small"
                                                fullWidth
                                                displayEmpty
                                                value={a.field ?? ""}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        field: e.target.value,
                                                    })
                                                }
                                                sx={inputSx}
                                            >
                                                <MenuItem
                                                    value=""
                                                    disabled
                                                    sx={{ fontSize: 13.5 }}
                                                >
                                                    Pick a field
                                                </MenuItem>
                                                {fields.map((f) => (
                                                    <MenuItem
                                                        key={f.key}
                                                        value={f.key}
                                                        sx={{ fontSize: 13.5 }}
                                                    >
                                                        {f.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="New value"
                                                value={a.value ?? ""}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        value: e.target.value,
                                                    })
                                                }
                                                InputProps={{ sx: inputSx }}
                                            />
                                        </Stack>
                                    )}

                                    {a.type === "send_email" && (
                                        <Stack sx={{ gap: 1.5 }}>
                                            <Select
                                                size="small"
                                                fullWidth
                                                value={a.to ?? "owner"}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        to: e.target.value,
                                                    })
                                                }
                                                sx={inputSx}
                                            >
                                                <MenuItem
                                                    value="owner"
                                                    sx={{ fontSize: 13.5 }}
                                                >
                                                    Record owner
                                                </MenuItem>
                                                <MenuItem
                                                    value="address"
                                                    sx={{ fontSize: 13.5 }}
                                                >
                                                    Specific address
                                                </MenuItem>
                                            </Select>
                                            {a.to === "address" && (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder="name@company.com"
                                                    value={a.address ?? ""}
                                                    onChange={(e) =>
                                                        updateAction(i, {
                                                            address:
                                                                e.target.value,
                                                        })
                                                    }
                                                    InputProps={{
                                                        sx: inputSx,
                                                    }}
                                                />
                                            )}
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Subject"
                                                value={a.subject ?? ""}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        subject:
                                                            e.target.value,
                                                    })
                                                }
                                                InputProps={{ sx: inputSx }}
                                            />
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                placeholder="Body"
                                                value={a.body ?? ""}
                                                onChange={(e) =>
                                                    updateAction(i, {
                                                        body: e.target.value,
                                                    })
                                                }
                                                InputProps={{ sx: inputSx }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: "var(--text-tertiary)",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                Merge tags work in the subject
                                                and body — e.g.{" "}
                                                {"{{"}
                                                {fields[0]?.key ?? "field_key"}
                                                {"}}"}.
                                            </Typography>
                                        </Stack>
                                    )}

                                    {a.type === "webhook" && (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="https://example.com/hook"
                                            value={a.url ?? ""}
                                            onChange={(e) =>
                                                updateAction(i, {
                                                    url: e.target.value,
                                                })
                                            }
                                            InputProps={{ sx: inputSx }}
                                        />
                                    )}

                                    {a.type === "create_record" && (
                                        <CreateRecordAction
                                            action={a}
                                            objects={objects}
                                            sourceObjectId={objectId}
                                            onChange={(patch) =>
                                                updateAction(i, patch)
                                            }
                                            inputSx={inputSx}
                                        />
                                    )}

                                    {actions.length > 1 && (
                                        <Stack
                                            onClick={() => {
                                                setActions((prev) =>
                                                    prev.filter(
                                                        (_a, ai) => ai !== i,
                                                    ),
                                                );
                                                setSelected(null);
                                                touch();
                                            }}
                                            sx={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 0.75,
                                                cursor: "pointer",
                                                color: "var(--error)",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                width: "fit-content",
                                            }}
                                        >
                                            <CloseRounded
                                                sx={{ fontSize: 15 }}
                                            />
                                            Remove action
                                        </Stack>
                                    )}
                                </Stack>
                            );
                        })()}

                    {selected == null && (
                        <Stack sx={{ p: "22px 24px", gap: 1.5 }}>
                            <Typography sx={PANEL_LABEL_SX}>
                                PROPERTIES
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13.5,
                                    lineHeight: 1.6,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                Click a node to configure it — WHEN picks the
                                trigger, IF narrows it with conditions, and
                                each THEN step is an action that runs.
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {!isNew && (
                <VersionHistoryDrawer
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    kind="workflow"
                    entityId={id}
                    title={name || "Workflow"}
                />
            )}
        </Stack>
    );
}
